<template>
  <div
    class="box-content flex flex-col items-center justify-center overflow-clip px-6 py-20 relative sm:min-h-[100px] text-center w-full"
  >
    <div
      class="-translate-x-1/2 -translate-y-1/2 absolute bg-primary/10 blur-[100px] h-[350px] left-1/2 rounded-full top-1/2 w-[350px]"
    />

    <div class="animate-in duration-700 fade-in relative z-(--z-index-ds-raised) zoom-in">
      <div class="flex group justify-center mb-10 overflow-visible pt-9 relative">
        <div
          class="absolute bg-primary/20 blur-[30px] h-32 rounded-full w-32"
          aria-hidden="true"
        />

        <div
          class="bg-surface-canvas/5 border border-border-inverse/10 dark:bg-surface-level-900/40 dark:border-border-level-700/50 flex h-24 items-center justify-center relative rounded-[2rem] safari-blur shadow-xl w-24"
        >
          <component
            :is="icon || CircleX"
            class="dark:text-primary-light duration-500 group-hover:rotate-12 h-12 text-primary transition-transform w-12"
            stroke-width="1.2"
          />
        </div>
      </div>

      <div class="max-w-lg mx-auto space-y-4">
        <h2
          class="dark:text-content-inverse font-extrabold md:text-5xl text-4xl text-content-level-900 tracking-tight"
        >
          {{ title }}
        </h2>

        <p
          class="dark:text-content-level-400 font-light leading-relaxed text-content-level-500 text-lg text-pretty"
        >
          {{ message }}
        </p>
      </div>

      <div class="flex flex-col gap-4 items-center justify-center mt-12 sm:flex-row w-full">
        <button
          v-if="primaryAction"
          type="button"
          class="active:scale-95 bg-primary cursor-pointer duration-300 font-semibold group hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-primary/40 inline-flex items-center justify-center min-w-[180px] px-8 py-3.5 rounded-2xl shadow-lg shadow-primary/25 text-content-inverse transition-all"
          @click="handlePrimaryAction"
        >
          <component
            :is="primaryAction.icon"
            v-if="primaryAction.icon"
            class="group-hover:animate-pulse h-5 mr-2 w-5"
          />
          {{ primaryAction.label }}
        </button>

        <button
          v-if="secondaryAction"
          type="button"
          class="active:scale-95 border-2 border-border-level-200 cursor-pointer dark:border-border-level-700 dark:hover:bg-surface-level-800/50 dark:hover:border-border-level-600 dark:text-content-level-300 duration-300 font-semibold hover:bg-surface-level-50 hover:border-border-level-300 inline-flex items-center justify-center min-w-[180px] px-8 py-3.5 rounded-2xl text-content-level-700 transition-all"
          @click="handleSecondaryAction"
        >
          <component
            :is="secondaryAction.icon"
            v-if="secondaryAction.icon"
            class="h-5 mr-2 w-5"
          />
          {{ secondaryAction.label }}
        </button>
      </div>
    </div>

    <span
      class="-bottom-10 absolute dark:text-content-inverse/[0.02] font-black pointer-events-none select-none text-[10rem] text-content-level-500/[0.03]"
    >
      {{ errorCode }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { CircleX, type LucideProps } from '@lucide/vue'
import type { FunctionalComponent } from 'vue'
import { usePageErrorState } from '~/composables/usePageErrorState'

interface Action {
  label: string
  onClick: () => void
  icon?: FunctionalComponent<LucideProps> | null
}

interface Props {
  /** Optional icon rendered above the error message. */
  icon?: FunctionalComponent<LucideProps> | null
  /** Error-state heading. */
  title: string
  /** Error-state explanatory message. */
  message: string
  /** Optional error code shown with the message. */
  errorCode?: string | number
  /** Optional primary recovery action. */
  primaryAction?: Action | null
  /** Optional secondary recovery action. */
  secondaryAction?: Action | null
}

const props = withDefaults(defineProps<Props>(), {
  errorCode: '',
  icon: null,
  primaryAction: null,
  secondaryAction: null
})

const { clearError } = usePageErrorState()

const handleAction = (action: Action | null) => {
  if (!action) {
    return
  }

  clearError()
  action.onClick()
}

const handlePrimaryAction = () => {
  handleAction(props.primaryAction)
}
const handleSecondaryAction = () => {
  handleAction(props.secondaryAction)
}
</script>

<style scoped>
.animate-in {
  animation: slideUp 0.7s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@supports (-webkit-backdrop-filter: blur(1px)) {
  .safari-blur {
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
  }
}
</style>
